import { reactive } from 'vue'

import {
  formatJsonOrYamlString,
  redirectToProxy,
} from '@scalar/oas-utils/helpers'
import type { OpenAPIV3_1 } from '@scalar/types/legacy'
import { parseDocument } from '@scalar-org/parser'

/** 5 minute cache expiry for spec permalinks */
const URL_CACHE_EXPIRY = 1000 * 60 * 1

export type ReferenceCache = Record<
  string,
  {
    spec: OpenAPIV3_1.Document
    raw: string
    lastUpdated: number
    hasLoaded?: boolean
  }
>

/**
 * Cache of pre-parsed reference data
 * This avoids processing large specification multiple times and
 * will cache URL base specs for a fixed amount of time
 *
 * URL specs will be updated silently in the background
 */
const referenceCache = reactive<ReferenceCache>({})

/**
 * Caches the loader, fetched, and parsed specification files
 * to prevent unnecessary parsing and network requests
 */
export function useReferenceCache(proxyUrl: string) {
  return {
    cache: referenceCache,
    /**
     * Load the static content
     * Exposed for direct call during SSR loading
     */
    loadSSRContent: (uid: string) =>
      // TODO: This should be moved back to loadContent when ApiReference SSG is working
      loadPartialSSRContent(uid, referenceCache, proxyUrl),
    /**
     * Fetch the permalink spec
     * Exposed for direct call during SSR loading
     */
    fetchSSRContent: (uid: string, permalink: string) =>
      fetchContent(uid, permalink, referenceCache, proxyUrl),
    /**
     * For static content loads we only ever try once
     * We return an initial loading state and kick of an async load for the
     * potentially much larger main content
     */
    getSpec: (uid: string) => {
      if (!referenceCache[uid]) {
        referenceCache[uid] = generateLoadingSpec()
        loadContent(uid, referenceCache, proxyUrl)
      }

      return referenceCache[uid]
    },
    /**
     * For permalink specs we do an initial fetch and then a periodic refresh
     */
    getPermalinkSpec(uid: string, permalink: string) {
      if (!referenceCache[uid]) {
        console.info('[Initial Fetching]: ', uid, permalink)
        referenceCache[uid] = generateLoadingSpec()
        fetchContent(uid, permalink, referenceCache, proxyUrl)
      }

      // Periodically refresh the permalink spec silently
      if (referenceCache[uid]?.lastUpdated + URL_CACHE_EXPIRY < Date.now()) {
        console.info('[Refreshing]: ', uid, permalink)
        fetchContent(uid, permalink, referenceCache, proxyUrl)
      }

      return referenceCache[uid]
    },
  }
}

/**
 * Create a mock spec to populate some loading state
 */
function generateLoadingSpec(url?: string) {
  const spec: OpenAPIV3_1.Document = {
    openapi: '3.1.0',
    info: {
      title: 'Loading...',
      description: url ? `Fetching spec from ${url}` : '',
    },
  }
  return {
    spec,
    raw: JSON.stringify(spec),
    lastUpdated: Date.now(),
  }
}

/**
 * Error data for when we fail to fetch or parse a remote URL or the local spec file
 */
function generateFallbackSpec(issueType: 'fetch' | 'parse') {
  const spec: OpenAPIV3_1.Document = {
    openapi: '3.1.0',
    info: {
      title:
        issueType === 'fetch'
          ? 'Error: Invalid file URL'
          : 'Error: Invalid OpenAPI File',
      description:
        issueType === 'fetch'
          ? 'Unable to fetch the document.'
          : 'Unable to parse a valid OpenAPI document from the file.',
    },
  }
  return {
    spec,
    raw: JSON.stringify(spec),
    lastUpdated: Date.now(),
  }
}

/**
 * In the conventional mode we load the spec from a static file
 */
async function loadContent(
  uid: string,
  cache: ReferenceCache,
  _proxyUrl: string,
) {
  try {
    const loaded = await import(`@/assets/${uid}.js`).catch(() => {
      throw Error(`Unable to load document at @/assets/${uid}.js`, {
        cause: 'import-error',
      })
    })

    const rawSpec = loaded.data.content

    console.info(
      `[Loading]: OpenAPI document ${uid}. Length: ${rawSpec.length}`,
    )

    const parsed = await parseDocument(rawSpec)

    setSpecToCache(uid, parsed.spec, rawSpec, cache)
  } catch (e) {
    cache[uid] = generateFallbackSpec(
      (e as Error)?.cause === 'import-error' ? 'fetch' : 'parse',
    )
    console.error('Error loading static data', uid)
    console.error(e)
  }
}

/**
 * Loads a simplified version of the spec for SSR
 * This is a temporary solution until ApiReference SSG is working
 */
async function loadPartialSSRContent(
  uid: string,
  cache: ReferenceCache,
  _proxy: string,
) {
  try {
    const loaded = await import(`@/assets/${uid}.js`).catch(() => {
      throw Error(`Unable to load document at @/assets/${uid}.js`, {
        cause: 'import-error',
      })
    })

    const rawSpec = loaded.data.content

    console.info(
      `[Loading]: OpenAPI document ${uid}. Length: ${rawSpec.length}`,
    )

    const parsed = await parseDocument(rawSpec)

    setSpecToCache(uid, parsed.spec, rawSpec, cache)
  } catch (e) {
    cache[uid] = generateFallbackSpec(
      (e as Error)?.cause === 'import-error' ? 'fetch' : 'parse',
    )
    console.error('Error loading static data', uid)
    console.error(e)
  }
}

/**
 * Fetches an OpenAPI/Swagger document from a given URL
 *
 * @throws an error if the fetch fails
 */
export async function fetchDocument(
  url: string,
  proxyUrl?: string,
  prettyPrint = true,
): Promise<string> {
  const response = await fetch(redirectToProxy(proxyUrl, url), {
    credentials: 'same-origin',
  })

  // Looks like the request failed
  if (response.status !== 200) {
    console.error(
      `[fetchDocument] Failed to fetch the OpenAPI document from ${url} (Status: ${response.status})`,
    )

    if (!proxyUrl) {
      console.warn(
        `[fetchDocument] Tried to fetch the OpenAPI document from ${url} without a proxy. Are the CORS headers configured to allow cross-domain requests? https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS`,
      )
    }

    throw new Error(
      `Failed to fetch the OpenAPI document from ${url} (Status: ${response.status})`,
    )
  }

  // If it’s JSON, make it pretty
  if (prettyPrint) return formatJsonOrYamlString(await response.text())

  return await response.text()
}

/**
 * In the permalink mode we fetch the spec from a remote URL
 */
async function fetchContent(
  uid: string,
  permalink: string,
  cache: ReferenceCache,
  proxy: string,
) {
  /**
   * Need to fetch the spec then use the references internal parser
   * to provide all the require formatting
   *
   * Hopefully we can streamline the references input at some point
   */
  const result = await fetchDocument(permalink, proxy).catch(() => {
    if (cache[uid]?.spec.info?.title?.includes('Loading')) {
      cache[uid] = generateFallbackSpec('fetch')
    }
    return ''
  })

  /**
   * If we have a good response then attempt parsing
   *
   * We only show the error pages if have not previously fetched a valid spec
   * This avoids going back to an error state after a previous successful fetch
   */
  if (result) {
    const parsed = await parseDocument(result)

    if (!parsed.valid && cache[uid]?.spec.info?.title?.includes('Loading')) {
      cache[uid] = generateFallbackSpec('parse')
    }

    setSpecToCache(uid, parsed.spec, result, cache)
  }
}

/** Load a spec */
function setSpecToCache(
  uid: string,
  spec: OpenAPIV3_1.Document | undefined | null,
  raw: string,
  cache: ReferenceCache,
) {
  if (!spec) return

  cache[uid] = {
    spec,
    raw,
    lastUpdated: Date.now(),
    hasLoaded: true,
  }
}
